import Select from "../../../components/Select";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { useAuth } from "../../Auth/useAuth";
import type { IActiveHospital } from "../types";

import type { IOption } from "../../../components/Select/types";
import { useHospitalList } from "../../../hooks/useHospitalList";
import { registerChangeHospitalLog } from "./utils";
import { useUsers } from "../../Users/useUsers";
import api from "../../../services/api";
import { toast } from "react-toastify";
import tokenService from "../../../services/token_service";
import { useQueryClient } from "@tanstack/react-query";
import GenericModal from "../../../components/GenericModal";

const ChangeActiveHospitalModal = ({
  changeActiveHospitalIsOpen,
  setChangeActiveHospitalIsOpen,
  activeHospital,
  setActiveHospital,
}: {
  changeActiveHospitalIsOpen: boolean;
  setChangeActiveHospitalIsOpen: Dispatch<SetStateAction<boolean>>;
  activeHospital: IActiveHospital | undefined;
  setActiveHospital: Dispatch<SetStateAction<IActiveHospital | undefined>>;
}) => {
  const auth = useAuth();
  const { getUserByEmail } = useUsers();
  const {
    data: hospitals,
    isLoading: isHospitalsLoading,
    error: hospitalsError,
  } = useHospitalList(auth.user?.id ?? 0);
  const queryClient = useQueryClient();

  const hasAutoSelectedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const shouldShowModal = useCallback(() => {
    return (
      changeActiveHospitalIsOpen &&
      auth.user?.status !== 5 &&
      auth.user?.role_id !== 5
    );
  }, [changeActiveHospitalIsOpen, auth.user?.status, auth.user?.role_id]);

  const hospitalOptions: IOption[] = useMemo(() => {
    if (!hospitals || hospitals.length === 0) {
      return [];
    }
    return hospitals.map((hospital) => ({
      value: hospital.uuid,
      label: hospital.name,
    }));
  }, [hospitals]);

  const defaultValue = useMemo(() => {
    if (!activeHospital?.uuid) return undefined;
    return {
      value: activeHospital.uuid,
      label: activeHospital.name,
    };
  }, [activeHospital?.uuid, activeHospital?.name]);

  const switchUserForDoctor = useCallback(
    async (filteredUser: any) => {
      try {
        const response = await api.post("/auth/switch-user", {
          userId: filteredUser.id,
          role_id: filteredUser.role_id,
          permissions: filteredUser.permissoes,
        });

        localStorage.setItem("access_token", response.data.token);
        tokenService.setTokens(response.data.token);
        return true;
      } catch (error) {
        console.error("Failed to refresh token:", error);
        toast.error("Erro ao trocar de hospital. Faça login novamente.");
        window.location.href = "/login";
        return false;
      }
    },
    [],
  );

  const prepareHospital = useCallback(
    (hospital: any): IActiveHospital | undefined => {
      if (!hospital?.id) return undefined;

      return {
        id: parseInt(hospital.id, 10),
        name: hospital?.name ?? "",
        uuid: hospital?.uuid ?? "",
        permissions: hospital.permissions ?? [],
        isProcedenciaActive: hospital?.isProcedenciaActive ?? false,
      };
    },
    [],
  );

  const changeActiveHospital = useCallback(
    async (uuid: string) => {
      // Cancel previous requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      try {
        if (!uuid || !hospitals) {
          console.warn("Invalid uuid or hospitals not loaded");
          return;
        }

        const hospital = hospitals.find((h) => h.uuid === uuid);
        if (!hospital) {
          console.error("No hospital found for UUID:", uuid);
          toast.error("Hospital não encontrado");
          return;
        }

        const hasHospitalChanged =
          !activeHospital?.id ||
          (hospital?.id && hospital.id !== activeHospital.id.toString());

        if (hasHospitalChanged) {
          registerChangeHospitalLog(uuid);
        }

        const newActiveHospital = prepareHospital(hospital);
        setActiveHospital(newActiveHospital);

        if (auth.user?.role_id === 2) {
          try {
            const users = await getUserByEmail({
              email: auth.user?.email ?? "",
            });

            const filtered = users?.find(
              (user) => Number(user.hospital) === Number(hospital?.id),
            );

            if (!filtered) {
              console.error("No user found for hospital:", hospital?.id);
              setActiveHospital(undefined);
              toast.error("Usuário não encontrado para este hospital");
              return;
            }

            auth.setUser(filtered);
            localStorage.removeItem("auth:user");
            localStorage.setItem("auth:user", JSON.stringify(filtered));
            localStorage.setItem("User_id_senne", filtered.id.toString());

            const success = await switchUserForDoctor(filtered);
            if (!success) {
              return;
            }
          } catch (error) {
            console.error("Error switching user for doctor:", error);
            toast.error("Erro ao atualizar perfil do médico");
            return;
          }
        }

        try {
          await queryClient.invalidateQueries({ queryKey: ["reports"] });
        } catch (error) {
          console.error("Error invalidating reports query:", error);
        }

        setChangeActiveHospitalIsOpen(false);
      } catch (error) {
        console.error("Error changing active hospital:", error);
        toast.error("Erro ao alterar hospital");
        setActiveHospital(undefined);
      }
    },
    [
      hospitals,
      activeHospital?.id,
      auth,
      prepareHospital,
      switchUserForDoctor,
      getUserByEmail,
      setActiveHospital,
      setChangeActiveHospitalIsOpen,
      queryClient,
    ],
  );

  useEffect(() => {
    if (
      hospitals?.length === 1 &&
      !hasAutoSelectedRef.current &&
      changeActiveHospitalIsOpen &&
      !activeHospital?.id // Don't auto-select if a hospital is already active
    ) {
      hasAutoSelectedRef.current = true;
      changeActiveHospital(hospitals[0].uuid);
    }
  }, [hospitals, changeActiveHospitalIsOpen, changeActiveHospital, activeHospital?.id]);

  useEffect(() => {
    if (!changeActiveHospitalIsOpen) {
      hasAutoSelectedRef.current = false;
    }
  }, [changeActiveHospitalIsOpen]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (hospitalsError) {
      console.error("Error loading hospitals:", hospitalsError);
      toast.error("Erro ao carregar hospitais");
    }
  }, [hospitalsError]);

  const handleSelectChange = useCallback(
    (option: IOption | undefined) => {
      if (option?.value) {
        const uuid =
          typeof option.value === "number"
            ? option.value.toString()
            : option.value;
        changeActiveHospital(uuid);
      }
    },
    [changeActiveHospital],
  );

  const handleModalClose = useCallback(() => {
    setChangeActiveHospitalIsOpen(false);
  }, [setChangeActiveHospitalIsOpen]);

  return (
    <GenericModal
      onClose={handleModalClose}
      isOpen={shouldShowModal()}
      size="md"
      isCentered
      closeOnEsc={true}
      closeOnOverlayClick={true}
      showCloseButton={true}
      header={<span>Selecione o hospital que deseja ter acesso</span>}
    >
      <div className="">
        <main>
          <Select
            options={hospitalOptions}
            closeOnSelect
            defaultValue={defaultValue}
            onChange={handleSelectChange}
            javascriptSearch
            isLoading={isHospitalsLoading}
            disabled={isHospitalsLoading || hospitalOptions.length === 0}
            theme="legacy"
          />
        </main>
      </div>
    </GenericModal>
  );
};

export default ChangeActiveHospitalModal;
